import React from 'react';
import type { Report, ReportsListLabels } from '../../../shared/types/reports';

export interface ReportCardProps {
  report: Report;
  reportUrl: string;
  labels: ReportsListLabels;
}

export function ReportCard({ report, reportUrl, labels }: ReportCardProps) {
  const periodLabel = report.periodType === 'week'
    ? labels.weeklyReports
    : labels.monthlyReports;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const moodChange = report.moodChange;
  const moodChangeDisplay = moodChange !== null
    ? (moodChange > 0 ? `+${moodChange.toFixed(1)}` : moodChange.toFixed(1))
    : null;

  const moodChangeColor = moodChange !== null
    ? (moodChange > 0 ? 'text-success' : moodChange < 0 ? 'text-danger' : 'text-muted')
    : '';

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body d-flex flex-column">
        {/* Header with badges and date */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex gap-1">
            <span className="badge bg-primary">
              {periodLabel}
            </span>
            {report.unviewed && (
              <span className="badge bg-success">
                {labels.newBadge}
              </span>
            )}
          </div>
          <small className="text-muted">
            <i className="bi bi-calendar-check me-1"></i>
            {formatShortDate(report.generatedAt)}
          </small>
        </div>

        {/* Title from AI report */}
        <h5 className="card-title mb-3 fw-bold">
          {report.aiReport?.title || 'AI Insights & Recommendations'}
        </h5>

        {/* Period dates */}
        <div className="mb-3">
          <p className="card-text text-muted mb-0">
            <small>
              {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}
            </small>
          </p>
        </div>

        {/* Mood stats summary */}
        {report.currentPeriodStats && (
          <div className="d-flex gap-3 mb-3 text-muted small">
            {report.currentPeriodStats.averageMood !== null && (
              <span>
                <i className="bi bi-emoji-smile me-1"></i>
                {report.currentPeriodStats.averageMood.toFixed(0)}%
              </span>
            )}
            <span>
              <i className="bi bi-calendar-check me-1"></i>
              {report.currentPeriodStats.moodCount} {labels.entries}
            </span>
            {moodChangeDisplay && (
              <span className={moodChangeColor}>
                <i className={`bi ${moodChange! > 0 ? 'bi-arrow-up' : moodChange! < 0 ? 'bi-arrow-down' : 'bi-dash'} me-1`}></i>
                {moodChangeDisplay}%
              </span>
            )}
          </div>
        )}

        {/* View button */}
        <div className="mt-auto">
          <a href={reportUrl} className="btn btn-primary w-100">
            {labels.viewReport}
          </a>
        </div>
      </div>
    </div>
  );
}

export default ReportCard;
